import React, { useState, useEffect } from 'react';
import { BookIndex, BookVerse } from '../types/book';
import { BookApiService } from '../services/bookApi';
import { TTSApiService } from '../services/ttsApi';
import { ReadApiService } from '../services/readApi';

interface VerseListProps {
  book: BookIndex;
  chapter: number;
  onBack: () => void;
  onGoHome: () => void;
  onPreviousChapter: () => void;
  onNextChapter: () => void;
}

const VerseList: React.FC<VerseListProps> = ({ book, chapter, onBack, onGoHome, onPreviousChapter, onNextChapter }) => {
  const [verses, setVerses] = useState<BookVerse[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [playingVerse, setPlayingVerse] = useState<number | null>(null);
  const [loadingVerse, setLoadingVerse] = useState<number | null>(null);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [showTranslation, setShowTranslation] = useState<number | null>(null);

  // HOME 버튼 클릭 핸들러 (모바일 렌더링 문제 해결)
  const handleGoHome = () => {
    if (isMobile) {
      // 모바일에서 스크롤 후 렌더링 문제 해결을 위한 강제 리렌더링
      setTimeout(() => {
        window.scrollTo(0, 0);
        // 뷰포트 강제 업데이트
        const viewport = document.querySelector('meta[name="viewport"]');
        if (viewport) {
          viewport.setAttribute('content', 'width=device-width, initial-scale=1.0');
        }
      }, 50);
    }
    onGoHome();
  };

  // PREV/NEXT 버튼 클릭 핸들러 (스크롤 맨 위로 이동)
  const handlePreviousChapter = () => {
    window.scrollTo(0, 0);
    onPreviousChapter();
  };

  const handleNextChapter = () => {
    window.scrollTo(0, 0);
    onNextChapter();
  };
  const [isMaleVoice, setIsMaleVoice] = useState<boolean>(() => {
    const saved = localStorage.getItem('book_tts_isMaleVoice');
    return saved !== null ? JSON.parse(saved) : false; // 디폴트: 여성
  });
  const [speakingRate, setSpeakingRate] = useState<number>(() => {
    const saved = localStorage.getItem('book_tts_speakingRate');
    return saved !== null ? JSON.parse(saved) : 1.0; // 디폴트: 1.0x
  });
  const [isDarkMode, setIsDarkMode] = useState<boolean>(() => {
    const saved = localStorage.getItem('book_dark_mode');
    return saved !== null ? JSON.parse(saved) : false; // 디폴트: 라이트 모드
  });
  const [readStatus, setReadStatus] = useState<Set<number>>(new Set());

  useEffect(() => {
    const fetchVerses = async () => {
      try {
        setLoading(true);
        setError(null);
        // API 호출
        const chapterData = await BookApiService.getBookChapter(book.bookNo, chapter);
        setVerses(chapterData.verses || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : '알 수 없는 오류가 발생했습니다.');
        // API 오류 시 mock 데이터 사용
        setVerses([
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchVerses();
  }, [book, chapter]);

  // 화면 크기 감지
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);

    return () => {
      window.removeEventListener('resize', checkIsMobile);
    };
  }, []);

  // 모바일에서 렌더링 문제 해결을 위한 강제 리렌더링
  useEffect(() => {
    if (isMobile) {
      // 컴포넌트 마운트 후 강제 리렌더링
      const timer = setTimeout(() => {
        window.scrollTo(0, 0);
        // 뷰포트 강제 업데이트
        const viewport = document.querySelector('meta[name="viewport"]');
        if (viewport) {
          viewport.setAttribute('content', 'width=device-width, initial-scale=1.0');
        }
      }, 50);

      return () => clearTimeout(timer);
    }
  }, [isMobile]);

  // 읽음 상태 로드
  useEffect(() => {
    const loadReadStatus = async () => {
      if (verses.length === 0) return;
      
      try {
        const response = await ReadApiService.getReadStatus(book.bookNo, chapter);
        const readVerses = response.verseList || [];
        setReadStatus(new Set(readVerses));
      } catch (error) {
        console.error('읽음 상태 조회 중 오류:', error);
        // API 오류 시 빈 Set으로 초기화
        setReadStatus(new Set());
      }
    };
    
    loadReadStatus();
  }, [verses, book.bookNo, chapter]);

  // 설정 변경 시 로컬 스토리지에 저장
  useEffect(() => {
    localStorage.setItem('book_tts_isMaleVoice', JSON.stringify(isMaleVoice));
  }, [isMaleVoice]);

  useEffect(() => {
    localStorage.setItem('book_tts_speakingRate', JSON.stringify(speakingRate));
  }, [speakingRate]);


  useEffect(() => {
    localStorage.setItem('book_dark_mode', JSON.stringify(isDarkMode));
  }, [isDarkMode]);

  // 다크모드 토글 핸들러
  const handleDarkModeToggle = () => {
    setIsDarkMode(!isDarkMode);
  };

  // 해당 장의 읽음 상태 삭제 핸들러
  const handleDeleteChapterReadStatus = async () => {
    if (window.confirm(`이 장의 읽음 상태를 모두 삭제하시겠습니까?`)) {
      try {
        await ReadApiService.deleteChapterReadStatus(book.bookNo, chapter);
        setReadStatus(new Set());
        alert('읽음 상태가 삭제되었습니다.');
      } catch (error) {
        console.error('읽음 상태 삭제 중 오류:', error);
        // 오류 시 화면에 표시하지 않고 콘솔에만 출력
      }
    }
  };

  // 번역 토글 핸들러
  const handleTranslationToggle = (verseNumber: number, event: React.MouseEvent) => {
    event.stopPropagation(); // 절 클릭 이벤트 방지
    setShowTranslation(showTranslation === verseNumber ? null : verseNumber);
  };

  // 컴포넌트 언마운트 시 재생 중인 음성 정지
  useEffect(() => {
    return () => {
      TTSApiService.stopCurrentAudio();
    };
  }, []);

  // TTS 재생 완료 이벤트 수신
  useEffect(() => {
    const handleTTSPlaybackEnded = () => {
      setPlayingVerse(null);
    };

    window.addEventListener('ttsPlaybackEnded', handleTTSPlaybackEnded);
    
    return () => {
      window.removeEventListener('ttsPlaybackEnded', handleTTSPlaybackEnded);
    };
  }, []);

  const handleVerseClick = async (verse: BookVerse) => {
    try {
      // 같은 절이 재생 중인 경우 재생 중지
      const verseNumber = verses.findIndex(v => v === verse) + 1;
      if (playingVerse === verseNumber) {
        TTSApiService.stopCurrentAudio();
        setPlayingVerse(null);
        setLoadingVerse(null);
        return;
      }

      // 로딩 중인 경우 중복 호출 방지
      if (loadingVerse !== null) {
        return;
      }

      // 현재 재생 중인 음성 완전히 정지
      TTSApiService.stopCurrentAudio();
      setPlayingVerse(null);
      setLoadingVerse(null);

      // 약간의 지연을 두어 이전 오디오가 완전히 중지되도록 함
      await new Promise(resolve => setTimeout(resolve, 100));

      setLoadingVerse(verseNumber);

      // 절 내용만 추출 (절 번호 제외)
      const textToSpeak = verse.content;
      
      // 언어 코드 설정 (book.lang에 따라)
      let languageCode: string;
      switch (book.lang) {
        case 'zh':
          languageCode = 'cmn-CN';
          break;
        case 'ja':
          languageCode = 'ja-JP';
          break;
        case 'ko':
          languageCode = 'ko-KR';
          break;
        case 'en':
        default:
          languageCode = 'en-US';
          break;
      }
      
      // Book 코드 매핑 (간단한 예시)
      const bookCodeMap: { [key: number]: string } = {
        1: 'Genesis', 2: 'Exodus', 3: 'Leviticus', 4: 'Numbers', 5: 'Deuteronomy',
        6: 'Joshua', 7: 'Judges', 8: 'Ruth', 9: '1Samuel', 10: '2Samuel',
        11: '1Kings', 12: '2Kings', 13: '1Chronicles', 14: '2Chronicles',
        15: 'Ezra', 16: 'Nehemiah', 17: 'Esther', 18: 'Job', 19: 'Psalms',
        20: 'Proverbs', 21: 'Ecclesiastes', 22: 'SongofSongs', 23: 'Isaiah',
        24: 'Jeremiah', 25: 'Lamentations', 26: 'Ezekiel', 27: 'Daniel',
        28: 'Hosea', 29: 'Joel', 30: 'Amos', 31: 'Obadiah', 32: 'Jonah',
        33: 'Micah', 34: 'Nahum', 35: 'Habakkuk', 36: 'Zephaniah',
        37: 'Haggai', 38: 'Zechariah', 39: 'Malachi', 40: 'Matthew',
        41: 'Mark', 42: 'Luke', 43: 'John', 44: 'Acts', 45: 'Romans',
        46: '1Corinthians', 47: '2Corinthians', 48: 'Galatians', 49: 'Ephesians',
        50: 'Philippians', 51: 'Colossians', 52: '1Thessalonians', 53: '2Thessalonians',
        54: '1Timothy', 55: '2Timothy', 56: 'Titus', 57: 'Philemon',
        58: 'Hebrews', 59: 'James', 60: '1Peter', 61: '2Peter', 62: '1John',
        63: '2John', 64: '3John', 65: 'Jude', 66: 'Revelation'
      };
      const bookCode = bookCodeMap[book.bookNo] || `book${book.bookNo}`;
      
      // 읽음 상태 저장 (API 호출) - 오류 시 콘솔에만 출력
      try {
        await ReadApiService.markAsRead(book.bookNo, chapter, verse.verse);
        
        // 읽음 상태 업데이트
        setReadStatus(prev => new Set(Array.from(prev).concat(verse.verse)));
      } catch (error) {
        console.error('읽음 상태 저장 중 오류:', error);
        // 오류가 발생해도 UI는 업데이트 (낙관적 업데이트)
        setReadStatus(prev => new Set(Array.from(prev).concat(verse.verse)));
      }
      
      // TTS API 호출 (성별, 속도, 언어, Book 정보 포함)
      await TTSApiService.speakText(
        textToSpeak, 
        isMaleVoice, 
        speakingRate, 
        languageCode,
        bookCode.toLowerCase(),
        chapter,
        verseNumber
      );
      setLoadingVerse(null);
      setPlayingVerse(verseNumber);
    } catch (error) {
      console.error('TTS 재생 중 오류가 발생했습니다:', error);
      setLoadingVerse(null);
      setPlayingVerse(null);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <div style={{ position: 'relative', marginBottom: '20px' }}>
          <button 
            onClick={onBack}
            style={{
              position: 'absolute',
              left: '0',
              top: '50%',
              transform: 'translateY(-50%)',
              padding: '8px 16px',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              zIndex: 10
            }}
          >
            ← 이전
          </button>
          <h2 style={{ 
            margin: 0, 
            color: book.bookNo <= 39 ? '#8b4513' : '#1e40af',
            textAlign: 'center',
            paddingLeft: '100px',
            paddingRight: '100px'
          }}>
            {book.name} {chapter}장
          </h2>
        </div>
        <p>Book을 불러오는 중...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '20px' }}>
        <div style={{ position: 'relative', marginBottom: '20px' }}>
          <button 
            onClick={onBack}
            style={{
              position: 'absolute',
              left: '0',
              top: '50%',
              transform: 'translateY(-50%)',
              padding: '8px 16px',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              zIndex: 10
            }}
          >
            ← 이전
          </button>
          <h2 style={{ 
            margin: 0, 
            color: book.bookNo <= 39 ? '#8b4513' : '#1e40af',
            textAlign: 'center',
            paddingLeft: '100px',
            paddingRight: '100px'
          }}>
            {book.name} {chapter}장
          </h2>
        </div>
        <div style={{ textAlign: 'center', color: 'red', padding: '20px' }}>
          <p>오류가 발생했습니다: {error}</p>
          <p>Mock 데이터를 표시합니다.</p>
        </div>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          {verses.map((verse, index) => (
            <div 
              key={index}
              onClick={() => handleVerseClick(verse)}
              style={{ 
                marginBottom: '15px',
                padding: '15px',
                backgroundColor: playingVerse === (index + 1) ? '#e3f2fd' : '#ffffff',
                borderRadius: '8px',
                border: playingVerse === (index + 1) ? '2px solid #2196f3' : '1px solid #e9ecef',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                animation: playingVerse === (index + 1) ? 'pulse 1.5s infinite' : 'none'
              }}
            >
              <p style={{ 
                margin: 0, 
                lineHeight: '1.6', 
                fontSize: '16px',
                textAlign: 'left',
                flex: 1
              }}>
                <strong>
                  {index + 1}.
                </strong>
                {' '}
                {verse.note && (
                  <span style={{ 
                    color: '#666',
                    fontSize: '14px',
                    fontStyle: 'italic',
                    marginRight: '8px'
                  }}>
                    ({verse.note})
                  </span>
                )}
                {verse.content}
                {loadingVerse === (index + 1) && (
                  <span style={{ 
                    marginLeft: '10px',
                    color: '#2196f3',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '5px'
                  }}>
                    <span style={{
                      width: '12px',
                      height: '12px',
                      border: '2px solid #2196f3',
                      borderTop: '2px solid transparent',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite',
                      display: 'inline-block'
                    }}></span>
                    로딩 중...
                  </span>
                )}
              </p>
              
              {/* 번역 버튼과 읽음 표시 */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                {/* 번역 버튼 */}
                {verse.translation && (
                  <button
                    onClick={(e) => handleTranslationToggle(index + 1, e)}
                    style={{
                      padding: '4px 8px',
                      backgroundColor: showTranslation === (index + 1) ? '#007bff' : 'transparent',
                      color: showTranslation === (index + 1) ? 'white' : '#6c757d',
                      border: `1px solid ${showTranslation === (index + 1) ? '#007bff' : '#dee2e6'}`,
                      borderRadius: '4px',
                      fontSize: '11px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      minWidth: '50px',
                      justifyContent: 'center'
                    }}
                    onMouseEnter={(e) => {
                      if (showTranslation !== (index + 1)) {
                        e.currentTarget.style.backgroundColor = '#f8f9fa';
                        e.currentTarget.style.borderColor = '#adb5bd';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (showTranslation !== (index + 1)) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.borderColor = '#dee2e6';
                      }
                    }}
                  >
                    번역
                  </button>
                )}
              </div>
              
              {/* 번역 툴팁 */}
              {showTranslation === (index + 1) && verse.translation && (
                <div style={{
                  position: 'absolute',
                  top: '-10px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  backgroundColor: '#333',
                  color: 'white',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  fontSize: '12px',
                  lineHeight: '1.4',
                  maxWidth: '300px',
                  wordWrap: 'break-word',
                  zIndex: 1000,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                  border: '1px solid #555',
                  pointerEvents: 'none'
                }}>
                  {verse.translation}
                  {verse.notation && (
                    <span>
                      {' '}({verse.notation})
                    </span>
                  )}
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: 0,
                    height: 0,
                    borderLeft: '6px solid transparent',
                    borderRight: '6px solid transparent',
                    borderTop: '6px solid #333'
                  }}></div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      padding: '20px',
      backgroundColor: isDarkMode ? '#1a1a1a' : '#ffffff',
      color: isDarkMode ? '#ffffff' : '#000000',
      minHeight: '100vh'
    }}>
      {/* 이전 버튼 - 헤더 바로 아래 왼쪽 정렬 */}
      <div style={{ marginBottom: '10px', textAlign: 'left' }}>
        <button 
          onClick={onBack}
          style={{
            padding: '8px 16px',
            backgroundColor: '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px'
          }}
        >
          ← 이전
        </button>
      </div>
      
      {/* Book 제목 - 별도 줄 */}
      <div style={{ maxWidth: '800px', margin: '0 auto', marginBottom: '20px' }}>
        <h2 style={{ 
          margin: 0, 
          color: book.bookNo <= 39 ? '#8b4513' : '#1e40af',
          textAlign: 'center',
          fontSize: isMobile ? '1.0rem' : undefined,
          fontWeight: 'bold'
        }}>
          {book.name} {chapter}{book.name === '시편' ? '편' : '장'}
        </h2>
      </div>
      
      {/* TTS 설정 패널 */}
      <div style={{ 
        maxWidth: '800px',
        margin: '0 auto',
        padding: '5px 15px', 
        backgroundColor: isDarkMode ? '#2d2d2d' : '#ffffff', 
        borderRadius: '8px',
        border: `1px solid ${isDarkMode ? '#404040' : '#e9ecef'}`,
        boxShadow: isDarkMode ? '0 2px 4px rgba(0,0,0,0.3)' : '0 2px 4px rgba(0,0,0,0.1)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '15px',
        flexWrap: 'wrap',
        boxSizing: 'border-box'
      }}>
        {/* 성별 선택 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <label style={{ fontSize: '14px', fontWeight: 'bold', color: isDarkMode ? '#ffffff' : '#495057' }}>
            Voice
          </label>
          <div style={{ display: 'flex', gap: '5px' }}>
            <button
              onClick={() => setIsMaleVoice(false)}
              style={{
                padding: '6px 12px',
                backgroundColor: !isMaleVoice ? '#007bff' : '#e9ecef',
                color: !isMaleVoice ? 'white' : '#495057',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: 'bold'
              }}
            >
              여성
            </button>
            <button
              onClick={() => setIsMaleVoice(true)}
              style={{
                padding: '6px 12px',
                backgroundColor: isMaleVoice ? '#007bff' : '#e9ecef',
                color: isMaleVoice ? 'white' : '#495057',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: 'bold'
              }}
            >
              남성
            </button>
          </div>
        </div>
        
        {/* 속도 조절 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <label style={{ fontSize: '14px', fontWeight: 'bold', color: isDarkMode ? '#ffffff' : '#495057' }}>
            Speed
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button
              onClick={() => setSpeakingRate(Math.max(0.5, speakingRate - 0.1))}
              disabled={speakingRate <= 0.5}
              style={{
                width: '32px',
                height: '32px',
                backgroundColor: speakingRate <= 0.5 ? '#e9ecef' : '#dc3545',
                color: speakingRate <= 0.5 ? '#6c757d' : 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: speakingRate <= 0.5 ? 'not-allowed' : 'pointer',
                fontSize: '16px',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              −
            </button>
            <span style={{ 
              fontSize: '14px', 
              fontWeight: 'bold', 
              color: '#007bff',
              minWidth: '40px',
              textAlign: 'center',
              padding: '4px 8px',
              backgroundColor: isDarkMode ? '#404040' : '#f8f9fa',
              borderRadius: '4px',
              border: `1px solid ${isDarkMode ? '#606060' : '#dee2e6'}`
            }}>
              {speakingRate.toFixed(1)}x
            </span>
            <button
              onClick={() => setSpeakingRate(Math.min(2.0, speakingRate + 0.1))}
              disabled={speakingRate >= 2.0}
              style={{
                width: '32px',
                height: '32px',
                backgroundColor: speakingRate >= 2.0 ? '#e9ecef' : '#28a745',
                color: speakingRate >= 2.0 ? '#6c757d' : 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: speakingRate >= 2.0 ? 'not-allowed' : 'pointer',
                fontSize: '16px',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              +
            </button>
          </div>
        </div>
        
        {/* 다크모드 및 삭제 아이콘 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <button
            onClick={handleDarkModeToggle}
            style={{
              padding: '4px',
              backgroundColor: 'transparent',
              border: 'none',
              cursor: 'pointer',
              fontSize: '20px',
              borderRadius: '50%',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = isDarkMode ? '#ffffff20' : '#00000010';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            {isDarkMode ? '☀️' : '🌙'}
          </button>
          <button
            onClick={handleDeleteChapterReadStatus}
            style={{
              padding: '4px',
              backgroundColor: 'transparent',
              border: 'none',
              cursor: 'pointer',
              fontSize: '18px',
              borderRadius: '50%',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: isDarkMode ? '#ff6b6b' : '#dc3545'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = isDarkMode ? '#ff6b6b20' : '#dc354510';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
            title="이 장의 읽음 상태 삭제"
          >
            🗑️
          </button>
        </div>
      </div>
      
      <div style={{ maxWidth: '800px', margin: '20px auto 0 auto' }}>
        {verses.map((verse, index) => {
          const verseNumber = index + 1;
          const isRead = readStatus.has(verse.verse);
          
          return (
          <div 
            key={index}
            onClick={() => handleVerseClick(verse)}
            style={{ 
              marginBottom: '15px',
              padding: '15px',
              backgroundColor: playingVerse === verseNumber 
                ? (isDarkMode ? '#2d4a6b' : '#e3f2fd') 
                : (isDarkMode ? '#2d2d2d' : '#ffffff'),
              borderRadius: '8px',
              border: playingVerse === verseNumber ? '2px solid #2196f3' : `1px solid ${isDarkMode ? '#404040' : '#e9ecef'}`,
              boxShadow: isDarkMode ? '0 2px 4px rgba(0,0,0,0.3)' : '0 2px 4px rgba(0,0,0,0.1)',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              animation: playingVerse === verseNumber ? 'pulse 1.5s infinite' : 'none',
              position: 'relative'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
              <p style={{ 
                margin: 0, 
                lineHeight: '1.6', 
                fontSize: '16px',
                textAlign: 'left',
                flex: 1
              }}>
                <strong>
                  {verseNumber}.
                </strong>
                {' '}
                {verse.note && (
                  <span style={{ 
                    color: isDarkMode ? '#888' : '#666',
                    fontSize: '14px',
                    fontStyle: 'italic',
                    marginRight: '8px'
                  }}>
                    ({verse.note})
                  </span>
                )}
                {verse.content}
                {loadingVerse === verseNumber && (
                  <span style={{ 
                    marginLeft: '10px',
                    color: '#2196f3',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '5px'
                  }}>
                    <span style={{
                      width: '12px',
                      height: '12px',
                      border: '2px solid #2196f3',
                      borderTop: '2px solid transparent',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite',
                      display: 'inline-block'
                    }}></span>
                    로딩 중...
                  </span>
                )}
              </p>
              
              {/* 번역 버튼과 읽음 표시 */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                {/* 번역 버튼 */}
                {verse.translation && (
                  <button
                    onClick={(e) => handleTranslationToggle(verseNumber, e)}
                    style={{
                      padding: '4px 8px',
                      backgroundColor: showTranslation === verseNumber ? '#007bff' : 'transparent',
                      color: showTranslation === verseNumber ? 'white' : '#6c757d',
                      border: `1px solid ${showTranslation === verseNumber ? '#007bff' : '#dee2e6'}`,
                      borderRadius: '4px',
                      fontSize: '11px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      minWidth: '50px',
                      justifyContent: 'center'
                    }}
                    onMouseEnter={(e) => {
                      if (showTranslation !== verseNumber) {
                        e.currentTarget.style.backgroundColor = '#f8f9fa';
                        e.currentTarget.style.borderColor = '#adb5bd';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (showTranslation !== verseNumber) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.borderColor = '#dee2e6';
                      }
                    }}
                  >
                    번역
                  </button>
                )}
                
                {/* 읽음 체크 표시 */}
                {isRead && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '24px',
                    height: '24px',
                    backgroundColor: '#28a745',
                    borderRadius: '50%',
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: 'bold'
                  }}>
                    ✓
                  </div>
                )}
              </div>
            </div>
            
            {/* 번역 툴팁 */}
            {showTranslation === verseNumber && verse.translation && (
              <div style={{
                position: 'absolute',
                top: '-10px',
                left: '50%',
                transform: 'translateX(-50%)',
                backgroundColor: isDarkMode ? '#1a1a1a' : '#333',
                color: 'white',
                padding: '8px 12px',
                borderRadius: '6px',
                fontSize: '12px',
                lineHeight: '1.4',
                maxWidth: '300px',
                wordWrap: 'break-word',
                zIndex: 1000,
                boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                border: `1px solid ${isDarkMode ? '#404040' : '#555'}`,
                pointerEvents: 'none'
              }}>
                {verse.translation}
                {verse.notation && (
                  <span>
                    {' '}({verse.notation})
                  </span>
                )}
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: 0,
                  height: 0,
                  borderLeft: '6px solid transparent',
                  borderRight: '6px solid transparent',
                  borderTop: `6px solid ${isDarkMode ? '#1a1a1a' : '#333'}`
                }}></div>
              </div>
            )}
          </div>
          );
        })}
      </div>
      
      {/* 네비게이션 버튼들 */}
      <div style={{ 
        maxWidth: '800px', 
        margin: '30px auto 0 auto',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '15px'
      }}>
        {/* 왼쪽: 처음으로 가기 */}
        <div>
          <button
            onClick={handleGoHome}
            style={{
              padding: '12px 20px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 'bold',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '5px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#0056b3';
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#007bff';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
            }}
          >
            HOME
          </button>
        </div>
        
        {/* 오른쪽: 이전장/다음장 */}
        <div style={{ display: 'flex', gap: '10px' }}>
           <button
             onClick={handlePreviousChapter}
            style={{
              padding: '12px 20px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 'bold',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '5px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#0056b3';
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#007bff';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
            }}
          >
            PREV
          </button>
          
           <button
             onClick={handleNextChapter}
            style={{
              padding: '12px 20px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 'bold',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '5px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#0056b3';
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#007bff';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
            }}
          >
            NEXT
          </button>
        </div>
      </div>
    </div>
  );
};

export default VerseList;
